import { createRoute, redirect } from '@tanstack/react-router';
import { locationSuffix } from '../../lib/guards';
import { lazyPage } from '../common';
import { siteRoute } from '../layouts';

const pages = () => import('./pages');
const groupsComp = lazyPage(pages, 'CustomerGroupsHost');
const groupsBuilderComp = lazyPage(pages, 'CustomerGroupsBuilderHost');
const groupsQuickSetupComp = lazyPage(pages, 'CustomerGroupsQuickSetupHost');
const companiesOverviewComp = lazyPage(pages, 'CustomerCompaniesOverviewHost');
const companiesComp = lazyPage(pages, 'CompaniesHost');
const companyFormComp = lazyPage(pages, 'CompanyFormHost');
const sectionsComp = lazyPage(pages, 'SectionsHost');
const sectionFormComp = lazyPage(pages, 'SectionFormHost');
const customerDetailsComp = lazyPage(pages, 'CustomerDetailsHost');
const salesAgentsOverviewComp = lazyPage(pages, 'CustomerSalesAgentsOverviewHost');
const rolesComp = lazyPage(pages, 'RolesHost');
const roleFormComp = lazyPage(pages, 'RoleFormHost');

// /customers redirects to the groups page.
const customersIndexRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/$siteEnv/customers/groups', params });
  },
});

const customersGroupsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/groups',
  component: groupsComp,
  staticData: { title: 'customers.groups' },
});

const customersGroupsCreateRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/groups/create',
  component: groupsBuilderComp,
  staticData: { title: 'customers.group_create' },
});

const customersGroupsQuickSetupRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/groups/quick-setup',
  component: groupsQuickSetupComp,
  staticData: { title: 'customers.group_create' },
});

const customersGroupsEditRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/groups/edit/$slug',
  component: groupsBuilderComp,
  staticData: { title: 'customers.group_edit' },
});

const customerListRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/list',
  component: companiesOverviewComp,
  staticData: { title: 'customers.customers' },
});

const customersCompaniesRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/companies',
  component: companiesComp,
  staticData: { title: 'customers.companies' },
});

const customersCompaniesCreateRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/companies/create',
  component: companyFormComp,
  staticData: { title: 'customers.company_create' },
});

const customersCompaniesEditRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/companies/edit/$id',
  component: companyFormComp,
  staticData: { title: 'customers.company_edit' },
});

const customersCompaniesSectionsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/companies/$id/sections',
  component: sectionsComp,
  staticData: { title: 'customers.company_sections' },
});

const customersCompaniesSectionsCreateRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/companies/$id/sections/create',
  component: sectionFormComp,
  staticData: { title: 'customers.section_create' },
});

const customersCompaniesSectionsEditRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/companies/$id/sections/edit/$sectionId',
  component: sectionFormComp,
  staticData: { title: 'customers.section_edit' },
});

const salesAgentsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/agents',
  component: salesAgentsOverviewComp,
  staticData: { title: 'customers.agents' },
});

const customersDetailsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/list/details/$id',
  component: customerDetailsComp,
  staticData: { title: 'customers.customer_details' },
});

const agentsDetailsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/agents/details/$id',
  component: customerDetailsComp,
  staticData: { title: 'customers.agent_details' },
});

const customersRolesRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/roles',
  component: rolesComp,
  staticData: { title: 'customers.roles' },
});

const customersRolesCreateRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/roles/create',
  component: roleFormComp,
  staticData: { title: 'customers.role_create' },
});

const customersRolesEditRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/roles/edit/$slug',
  component: roleFormComp,
  staticData: { title: 'customers.role_edit' },
});

// Legacy `/v2/customers/*` URLs from before the v2 pages were promoted.
const legacyV2CustomersRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/$',
  beforeLoad({ location }) {
    throw redirect({
      href: `${location.pathname.replace('/v2/customers', '/customers')}${locationSuffix(location)}`,
    });
  },
});

/** Customers family leaves to attach under the site-env layout. */
export const customersLeaves = [
  customersIndexRoute,
  customersGroupsRoute,
  customersGroupsCreateRoute,
  customersGroupsEditRoute,
  customersGroupsQuickSetupRoute,
  customerListRoute,
  customersCompaniesRoute,
  customersCompaniesCreateRoute,
  customersCompaniesEditRoute,
  customersCompaniesSectionsRoute,
  customersCompaniesSectionsCreateRoute,
  customersCompaniesSectionsEditRoute,
  salesAgentsRoute,
  customersDetailsRoute,
  agentsDetailsRoute,
  customersRolesRoute,
  customersRolesCreateRoute,
  customersRolesEditRoute,
  legacyV2CustomersRoute,
];
